import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, photoSrc, rupiah } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import UserPill from "@/components/UserPill";

const CATS = [
  { key: "all",     label: "Semua",   test: () => true },
  { key: "makanan", label: "Makanan", test: (d) => /nasi|mie|ayam|makan|padang|bowl|sayur|salad|berkuah/i.test(d.category || "") },
  { key: "minuman", label: "Minuman", test: (d) => /minum|kopi|jus|teh|es /i.test(d.category || "") },
  { key: "snack",   label: "Snack",   test: (d) => /snack|camilan|kue|risoles|pisang|goreng/i.test(d.category || "") },
];
const SORTS = [
  { key: "populer",  label: "Paling sering dipesan", cmp: (a, b) => (b.sold_count || 0) - (a.sold_count || 0) },
  { key: "harga_up", label: "Harga terendah",         cmp: (a, b) => a.price - b.price },
  { key: "harga_dn", label: "Harga tertinggi",        cmp: (a, b) => b.price - a.price },
  { key: "stok",     label: "Sisa porsi terbanyak",   cmp: (a, b) => (b.portions || 0) - (a.portions || 0) },
];
const initials = (n) => (n || "?").split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase();

const useCountdown = (closeHour = "22.00") => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(t); }, []);
  const [h, m] = closeHour.split(".").map((n) => parseInt(n, 10));
  const target = new Date(now); target.setHours(h || 22, m || 0, 0, 0);
  if (target < now) target.setDate(target.getDate() + 1);
  const diff = Math.max(0, target - now);
  return { hh: Math.floor(diff / 3.6e6), mm: Math.floor((diff % 3.6e6) / 60000), closed: diff === 0 };
};

export default function Browse() {
  const { user } = useAuth();
  const { items, add, setQty, clear, count, subtotal } = useCart();
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [search, setSearch] = useState("");
  const [sisaOnly, setSisaOnly] = useState(false);
  const [sort, setSort] = useState("populer");
  const [drawer, setDrawer] = useState(false);

  useEffect(() => { api.get("/dishes").then(({ data }) => setDishes(data)).finally(() => setLoading(false)); }, []);
  const { hh, mm, closed } = useCountdown("22.00");

  const shown = useMemo(() => {
    let out = dishes.filter(CATS.find((c) => c.key === cat).test);
    if (sisaOnly) out = out.filter((d) => (d.portions || 0) > 0);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      out = out.filter((d) => [d.name, d.cook_name, d.category, d.kitchen_name].some((x) => (x || "").toLowerCase().includes(q)));
    }
    return [...out].sort(SORTS.find((s) => s.key === sort).cmp);
  }, [dishes, cat, sisaOnly, sort, search]);

  const cookCount = new Set(dishes.map((d) => d.cook_user_id)).size;
  const cookIndex = useMemo(() => {
    const m = {};
    dishes.forEach((d) => { m[d.dish_id] = { cook_user_id: d.cook_user_id, cook_name: d.cook_name }; });
    return m;
  }, [dishes]);
  const onAdd = (d) => { if ((d.portions || 0) <= 0) return toast.error("Menu ini sudah habis"); add(d); toast.success(`Ditambahkan: ${d.name}`); };
  const kitchenLabel = user?.office_name || "Kantor";
  const domain = (user?.email || "").split("@")[1] || "";

  return (
    <div style={{ minHeight: "100vh", background: "#FBEFD3", color: "#26221E", fontFamily: "'Nunito Sans', sans-serif" }}>
      <header style={{ position: "sticky", top: 0, zIndex: 30, background: "#FBEFD3", borderBottom: "1px solid rgba(38,34,30,0.10)", padding: "22px clamp(20px, 4vw, 56px)", display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
          {user?.is_guest ? (
            <a data-testid="logo-home" href="/" style={{ fontFamily: "'Bitter', Georgia, serif", fontWeight: 800, fontSize: 34, letterSpacing: "-0.02em", color: "#26221E", textDecoration: "none" }}>Cookingin</a>
          ) : (
            <span data-testid="logo-static" style={{ fontFamily: "'Bitter', Georgia, serif", fontWeight: 800, fontSize: 34, letterSpacing: "-0.02em", color: "#26221E" }}>Cookingin</span>
          )}
          <span style={{ width: 1, height: 30, background: "rgba(38,34,30,0.14)" }} />
          <div data-testid="browse-office-chip" style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 18px 8px 8px", background: "#1A17E0", color: "#fff", borderRadius: 999 }}>
            <span style={{ width: 36, height: 36, borderRadius: "50%", background: "#F0B429", color: "#26221E", display: "grid", placeItems: "center", fontWeight: 700, fontSize: 14 }}>{initials(kitchenLabel)}</span>
            <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
              <strong style={{ fontSize: 15 }}>{kitchenLabel}</strong>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, opacity: 0.85 }}>@{domain || "kantor.co.id"}</span>
            </span>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <UserPill variant="buyer" />
          <button data-testid="header-cart-btn" onClick={() => setDrawer(true)} style={{ padding: "12px 22px", borderRadius: 999, background: count ? "#E8823A" : "#26221E", color: "#fff", border: "none", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Keranjang · {count}</button>
        </div>
      </header>

      <div style={{ background: "#26221E", color: "#FBEFD3", padding: "10px clamp(16px,3vw,32px)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, fontSize: 12 }}>
        <span className="font-mono-c" style={{ display: "flex", alignItems: "center", gap: 8, letterSpacing: "0.06em" }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#5DE38A", boxShadow: "0 0 0 4px rgba(93,227,138,0.22)" }} />
          Email kantor terverifikasi @{domain || "—"} — kamu hanya melihat menu dari {kitchenLabel}
        </span>
        <span data-testid="browse-countdown" className="font-mono-c" style={{ color: "#F0B429", letterSpacing: "0.08em" }}>
          {closed ? "Pre-order hari ini sudah ditutup" : `Pre-order besok terkunci 22.00 · sisa ${hh}j ${mm}m`}
        </span>
      </div>

      <main style={{ maxWidth: 1560, margin: "0 auto", padding: "clamp(28px, 3vw, 44px) clamp(20px, 4vw, 56px) 80px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 20, flexWrap: "wrap", marginBottom: 24 }}>
          <div>
            <p className="font-mono-c" style={{ fontSize: 13, color: "#1A17E0", letterSpacing: "0.22em", margin: "0 0 12px", fontWeight: 500 }}>LINE UP MENU HARI INI</p>
            <h1 className="font-display" style={{ fontSize: "clamp(30px, 4vw, 50px)", letterSpacing: "-0.02em", margin: 0 }}>Menu hari ini di {kitchenLabel}</h1>
            <p style={{ color: "#6E6559", fontSize: 15, marginTop: 8, maxWidth: "56ch" }}>Ambil sendiri di pantry, tanpa ongkir. Hanya rekan sekantor di @{domain || "kantor.co.id"} yang bisa melihat menu ini.</p>
          </div>
          <div className="font-mono-c" style={{ fontSize: 12, color: "#6E6559", textAlign: "right", lineHeight: 1.6 }}>
            <div>{new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long" })}</div>
            <div>{dishes.length} MENU TAYANG · {cookCount} REKAN MASAK</div>
          </div>
        </div>

        <div style={{ background: "#fff", border: "1px solid rgba(38,34,30,0.10)", borderRadius: 14, padding: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 20 }}>
          <input data-testid="browse-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari menu, koki, divisi…" style={{ flex: "1 1 220px", padding: "10px 14px", border: "1px solid rgba(38,34,30,0.16)", borderRadius: 4, fontSize: 14, fontFamily: "inherit" }} />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {CATS.map((c) => (
              <button key={c.key} data-testid={`cat-${c.key}`} onClick={() => setCat(c.key)} style={{ padding: "8px 14px", borderRadius: 999, border: "1px solid " + (cat === c.key ? "#1A17E0" : "rgba(38,34,30,0.18)"), background: cat === c.key ? "#1A17E0" : "#fff", color: cat === c.key ? "#fff" : "#26221E", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>{c.label}</button>
            ))}
          </div>
          <button data-testid="sisa-toggle" onClick={() => setSisaOnly((v) => !v)} style={{ padding: "8px 14px", borderRadius: 999, border: "1px solid " + (sisaOnly ? "#E8823A" : "rgba(38,34,30,0.18)"), background: sisaOnly ? "#E8823A" : "#fff", color: sisaOnly ? "#fff" : "#26221E", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>Sisa porsi saja</button>
          <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} style={{ padding: "8px 12px", borderRadius: 4, border: "1px solid rgba(38,34,30,0.18)", fontSize: 13, background: "#fff", cursor: "pointer" }}>
            {SORTS.map((s) => <option key={s.key} value={s.key}>Urut: {s.label}</option>)}
          </select>
        </div>

        {loading && <p className="font-mono-c" style={{ color: "#9A9084" }}>Memuat menu…</p>}
        {!loading && shown.length === 0 && (
          <div style={{ padding: 40, border: "1px dashed rgba(38,34,30,0.24)", borderRadius: 14, textAlign: "center" }} data-testid="empty-state">
            <p className="font-display" style={{ fontSize: 20, fontWeight: 700, margin: "0 0 8px" }}>Belum ada menu yang cocok</p>
            <button onClick={() => { setCat("all"); setSearch(""); setSisaOnly(false); setSort("populer"); }} style={{ padding: "10px 18px", background: "#1A17E0", color: "#fff", border: "none", borderRadius: 4, fontWeight: 700, cursor: "pointer" }}>Reset filter</button>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }} data-testid="dish-grid">
          {shown.map((d, i) => {
            const soldOut = (d.portions || 0) <= 0;
            const inCart = items.find((x) => x.dish_id === d.dish_id);
            const stock = d.portions || 0;
            return (
              <article key={d.dish_id} data-testid={`dish-card-${d.dish_id}`} style={{ background: "#fff", borderRadius: 12, boxShadow: "0 8px 24px rgba(38,34,30,0.08)", border: inCart ? "2px solid #1A17E0" : "1px solid rgba(38,34,30,0.08)", overflow: "hidden", display: "flex", flexDirection: "column", opacity: soldOut ? 0.72 : 1 }}>
                <div style={{ position: "relative" }}>
                  <img src={photoSrc(d.photo_url)} alt={d.name} style={{ width: "100%", height: 210, objectFit: "cover", filter: soldOut ? "grayscale(0.7)" : "none" }} />
                  <span data-testid={`stock-${d.dish_id}`} style={{ position: "absolute", top: 12, right: 12, padding: "5px 10px", borderRadius: 999, background: soldOut ? "#B3261E" : "rgba(255,255,255,0.92)", color: soldOut ? "#fff" : "#26221E", fontSize: 11, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>{soldOut ? "Habis" : `Sisa ${stock} porsi`}</span>
                  {i === 0 && !soldOut && (d.sold_count || 0) > 0 && (
                    <span style={{ position: "absolute", bottom: 12, left: 0, padding: "5px 12px", borderRadius: "0 3px 3px 0", background: "#2F7A3D", color: "#fff", fontSize: 10, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: "0.1em", fontWeight: 500 }}>PALING SERING DIPESAN</span>
                  )}
                </div>
                <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
                  <h3 className="font-display" style={{ fontSize: 22, fontWeight: 800, margin: 0, lineHeight: 1.2 }}>{d.name}</h3>
                  <p className="font-mono-c" style={{ margin: 0, fontSize: 11, color: "#1A17E0", letterSpacing: "0.1em" }}>{(d.category || "MENU").toUpperCase()}</p>
                  <p style={{ margin: 0, fontSize: 13, color: "#6E6559", minHeight: 36 }}>{d.ingredients}</p>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4 }}>
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 18, fontWeight: 700 }}>{rupiah(d.price)}</span>
                    <span className="font-mono-c" style={{ fontSize: 11, color: "#6E6559" }}>Pantry L8 · 12.00</span>
                  </div>
                  <div style={{ height: 1, background: "rgba(38,34,30,0.08)", margin: "4px 0" }} />
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 26, height: 26, borderRadius: "50%", background: "#1A17E0", color: "#fff", display: "grid", placeItems: "center", fontSize: 10, fontWeight: 700 }}>{initials(d.cook_name)}</span>
                    <span style={{ fontSize: 12, color: "#6E6559" }}>{d.cook_name} · {d.kitchen_name || "Rekan Masak"}</span>
                  </div>
                  <div style={{ marginTop: "auto", paddingTop: 8 }}>
                    {soldOut ? (
                      <button disabled data-testid={`sold-${d.dish_id}`} style={{ width: "100%", minHeight: 50, background: "rgba(38,34,30,0.08)", color: "#6E6559", border: "none", borderRadius: 4, fontWeight: 700, cursor: "not-allowed", fontSize: 13 }}>Kuota habis · notifikasi besok</button>
                    ) : inCart ? (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#FBEFD3", border: "1px solid #1A17E0", borderRadius: 4, padding: 4 }}>
                        <button data-testid={`minus-${d.dish_id}`} onClick={() => setQty(d.dish_id, inCart.qty - 1)} style={{ width: 42, height: 42, background: "transparent", border: "none", cursor: "pointer", fontSize: 20, fontWeight: 700 }}>−</button>
                        <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700 }}>{inCart.qty} porsi</span>
                        <button data-testid={`plus-${d.dish_id}`} onClick={() => { if (inCart.qty >= stock) return toast.error(`Kuota ${d.name} tinggal ${stock} porsi`); setQty(d.dish_id, inCart.qty + 1); }} style={{ width: 42, height: 42, background: "transparent", border: "none", cursor: "pointer", fontSize: 20, fontWeight: 700 }}>+</button>
                      </div>
                    ) : (
                      <button data-testid={`add-${d.dish_id}`} onClick={() => onAdd(d)} style={{ width: "100%", minHeight: 50, background: "#E8823A", color: "#fff", border: "none", borderRadius: 4, fontWeight: 700, cursor: "pointer", fontSize: 14 }}>Tambah ke Keranjang</button>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        <div style={{ marginTop: 60, background: "#1A17E0", color: "#fff", borderRadius: 18, padding: "clamp(28px,4vw,44px)" }}>
          <p className="font-mono-c" style={{ color: "#F0B429", fontSize: 13, letterSpacing: "0.22em", margin: "0 0 12px", fontWeight: 500 }}>KENAPA CUMA MENU INI YANG TAMPIL</p>
          <h2 className="font-display" style={{ fontSize: "clamp(22px, 2.8vw, 32px)", letterSpacing: "-0.02em", margin: "0 0 12px" }}>Cookingin adalah ekosistem tertutup per domain email kantor.</h2>
          <p style={{ color: "rgba(255,255,255,0.82)", fontSize: 15, lineHeight: 1.6, maxWidth: "62ch", margin: 0 }}>Kamu masuk sebagai <strong>{user?.email || "tamu"}</strong>, jadi hanya melihat Rekan Masak di @{domain || "kantor.co.id"}. Kolega di gedung lain tidak bisa melihat pesananmu.</p>
        </div>
      </main>

      <CartDrawer open={drawer} onClose={() => setDrawer(false)} items={items} setQty={setQty} clear={clear} count={count} subtotal={subtotal} kitchen={kitchenLabel} domain={domain} closed={closed} cookIndex={cookIndex} />
    </div>
  );
}

function CartDrawer({ open, onClose, items, setQty, clear, count, subtotal, kitchen, domain, closed, cookIndex }) {
  const [notes, setNotes] = useState({});
  const [pickups, setPickups] = useState({}); // {cook_user_id: pickup_point_id}
  const [pickupOptions, setPickupOptions] = useState({});
  const [streak, setStreak] = useState(null);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  const cookIds = useMemo(() => {
    const ids = new Set();
    items.forEach((it) => { const c = cookIndex?.[it.dish_id]; if (c) ids.add(c.cook_user_id); });
    return [...ids];
  }, [items, cookIndex]);

  useEffect(() => {
    if (!open) return;
    api.get("/vouchers").then(({ data }) => setStreak(data.streak)).catch(() => {});
    if (cookIds.length) {
      api.get(`/pickup-points?cook_user_ids=${cookIds.join(",")}`).then(({ data }) => {
        const byCook = {};
        (data || []).forEach((p) => { (byCook[p.seller_user_id] ||= []).push(p); });
        setPickupOptions(byCook);
      }).catch(() => setPickupOptions({}));
    }
  }, [open, cookIds.join(",")]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const withNote = Object.values(notes).filter((n) => (n || "").trim().length > 0).length;

  const checkout = async () => {
    if (closed) return toast.error("Pre-order sudah ditutup. Coba lagi besok pagi.");
    const notesMap = Object.fromEntries(Object.entries(notes).filter(([, v]) => (v || "").trim()).map(([id, v]) => [id, v.trim()]));
    const pickupsMap = Object.fromEntries(Object.entries(pickups).filter(([, v]) => v));
    setSending(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ dish_id: i.dish_id, qty: i.qty })),
        notes: notesMap,
        pickups: pickupsMap,
      });
      const ids = (Array.isArray(data) ? data : [data]).map((o) => o.order_id).filter(Boolean);
      clear(); setNotes({}); setPickups({}); onClose();
      toast.success("Pesanan dikunci. Silakan bayar ke tiap Rekan Masak.");
      navigate(`/checkout?order_ids=${ids.join(",")}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal kunci pesanan"); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="cart-drawer-overlay" onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(38,34,30,0.5)", backdropFilter: "blur(2px)", display: "flex", justifyContent: "flex-end" }}>
      <aside data-testid="cart-drawer" onClick={(e) => e.stopPropagation()} style={{ width: "min(460px, 94vw)", height: "100vh", background: "#fff", display: "flex", flexDirection: "column", animation: "drawerIn 280ms cubic-bezier(.22,1,.36,1) both" }}>
        <div style={{ padding: "18px 22px", borderBottom: "1px solid rgba(38,34,30,0.10)", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div>
            <h2 className="font-display" style={{ fontSize: 24, fontWeight: 800, margin: 0 }}>Keranjang Pre-Order</h2>
            <p className="font-mono-c" style={{ fontSize: 11, color: "#6E6559", margin: "4px 0 0" }}>{kitchen} · @{domain || "kantor.co.id"}</p>
          </div>
          <button data-testid="drawer-close" onClick={onClose} style={{ width: 32, height: 32, borderRadius: "50%", border: "none", background: "rgba(38,34,30,0.08)", cursor: "pointer", fontSize: 14 }}>✕</button>
        </div>
        {streak && (
          <div data-testid="streak-banner" style={{ padding: "12px 20px", background: "linear-gradient(90deg, #F0B429 0%, #E8823A 100%)", color: "#26221E" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <strong style={{ fontFamily: "'Bitter', Georgia, serif", fontSize: 14 }}>Streak 🔥 {streak.current} hari</strong>
              <span className="font-mono-c" style={{ fontSize: 10, letterSpacing: "0.08em" }}>
                {streak.next_milestone ? `${streak.next_milestone - streak.current} hari lagi ke ${streak.next_milestone}d` : "MAX 20d ✓"}
              </span>
            </div>
            <div style={{ display: "flex", gap: 4, height: 6, background: "rgba(38,34,30,0.15)", borderRadius: 999, overflow: "hidden" }}>
              <div style={{ width: `${Math.min(100, streak.progress_to_next * 100)}%`, background: "#26221E", transition: "width 300ms" }} />
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              {streak.tiers.map((t) => (
                <span key={t.days} data-testid={`tier-${t.days}`} style={{ padding: "2px 8px", borderRadius: 999, background: t.unlocked ? "#26221E" : "rgba(38,34,30,0.14)", color: t.unlocked ? "#F0B429" : "#26221E", fontSize: 10, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace" }}>
                  {t.days}d · {t.discount_pct}%{t.unlocked ? " ✓" : ""}
                </span>
              ))}
            </div>
          </div>
        )}
        <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
          {count === 0 && (
            <div style={{ padding: 30, textAlign: "center", color: "#6E6559", border: "1px dashed rgba(38,34,30,0.24)", borderRadius: 10 }}>
              <p style={{ fontFamily: "'Bitter', Georgia, serif", fontWeight: 700, fontSize: 17, margin: "0 0 6px", color: "#26221E" }}>Keranjang masih kosong</p>
              <p style={{ margin: 0, fontSize: 13 }}>Pilih menu rekan sekantormu, pesanan terkunci jam 22.00 malam ini.</p>
            </div>
          )}
          {items.map((it) => {
            const n = notes[it.dish_id] || "";
            const overCap = n.length > 105;
            return (
              <div key={it.dish_id} data-testid={`drawer-line-${it.dish_id}`} style={{ padding: "12px 0", borderBottom: "1px solid rgba(38,34,30,0.08)" }}>
                <div style={{ display: "grid", gridTemplateColumns: "66px 1fr auto", gap: 10, alignItems: "center" }}>
                  <div style={{ width: 66, height: 66, borderRadius: 6, background: "rgba(38,34,30,0.06)" }} />
                  <div>
                    <strong style={{ fontSize: 14 }}>{it.name}</strong>
                    <div className="font-mono-c" style={{ fontSize: 10, color: "#6E6559", marginTop: 2 }}>{it.cook_name} · PANTRY L8</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 6 }}>
                      <button onClick={() => setQty(it.dish_id, it.qty - 1)} style={{ width: 26, height: 26, borderRadius: 4, border: "1px solid rgba(38,34,30,0.16)", background: "#fff", cursor: "pointer" }}>−</button>
                      <span style={{ minWidth: 22, textAlign: "center", fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, fontSize: 13 }}>{it.qty}</span>
                      <button onClick={() => setQty(it.dish_id, it.qty + 1)} style={{ width: 26, height: 26, borderRadius: 4, border: "1px solid rgba(38,34,30,0.16)", background: "#fff", cursor: "pointer" }}>+</button>
                    </div>
                  </div>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, fontSize: 14 }}>{rupiah(it.qty * it.price)}</span>
                </div>
                <div style={{ marginTop: 10 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                    <span className="font-mono-c" style={{ fontSize: 10, letterSpacing: "0.14em", color: "#6E6559" }}>CATATAN UNTUK {(it.cook_name || "").split(" ")[0].toUpperCase()}</span>
                    <span className="font-mono-c" style={{ fontSize: 10, color: overCap ? "#B3261E" : "#9A9084" }}>{n.length}/120</span>
                  </div>
                  <textarea data-testid={`note-${it.dish_id}`} value={n} maxLength={120} onChange={(e) => setNotes({ ...notes, [it.dish_id]: e.target.value })} placeholder="mis. tanpa sambal, nasi setengah, kemas terpisah" style={{ width: "100%", minHeight: 44, padding: 8, borderRadius: 4, border: `1px solid ${n ? "#1A17E0" : "rgba(38,34,30,0.16)"}`, background: n ? "#F1EEFF" : "#fff", fontFamily: "inherit", fontSize: 12, resize: "vertical" }} />
                </div>
              </div>
            );
          })}
        </div>
        {count > 0 && (
          <div style={{ borderTop: "1px solid rgba(38,34,30,0.10)", padding: 20, background: "#fff" }}>
            {cookIds.length > 0 && (
              <div data-testid="drawer-pickups" style={{ marginBottom: 12 }}>
                <p className="font-mono-c" style={{ fontSize: 10, letterSpacing: "0.14em", color: "#6E6559", margin: "0 0 6px" }}>TITIK AMBIL PER REKAN MASAK</p>
                {cookIds.map((cid) => {
                  const cookName = items.find((it) => cookIndex?.[it.dish_id]?.cook_user_id === cid)?.cook_name || "Rekan Masak";
                  const opts = pickupOptions[cid] || [];
                  return (
                    <div key={cid} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <span style={{ flex: "0 0 90px", fontSize: 12, fontWeight: 700 }}>{cookName}</span>
                      <select
                        data-testid={`pickup-${cid}`}
                        value={pickups[cid] || ""}
                        onChange={(e) => setPickups({ ...pickups, [cid]: e.target.value })}
                        style={{ flex: 1, padding: "6px 8px", border: "1px solid rgba(38,34,30,0.18)", borderRadius: 4, fontSize: 12, background: "#fff" }}
                      >
                        <option value="">{opts.length ? "Pilih titik ambil…" : "Rekan Masak belum daftar titik ambil"}</option>
                        {opts.map((p) => (<option key={p.id} value={p.id}>{p.name}{p.note ? ` — ${p.note}` : ""}</option>))}
                      </select>
                    </div>
                  );
                })}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}><span style={{ color: "#6E6559" }}>Subtotal</span><span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{rupiah(subtotal)}</span></div>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}><span style={{ color: "#6E6559" }}>Ongkos kirim</span><span style={{ fontFamily: "'IBM Plex Mono', monospace", color: "#2F7A3D" }}>Rp 0 · ambil di pantry</span></div>
            {withNote > 0 && <div style={{ fontSize: 12, color: "#6E6559", marginBottom: 4 }}>Catatan menu: {withNote} menu punya catatan khusus</div>}
            <div style={{ height: 1, background: "rgba(38,34,30,0.10)", margin: "10px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}><strong>Total bayar</strong><span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 24, fontWeight: 700, color: "#1A17E0" }}>{rupiah(subtotal)}</span></div>
            <button data-testid="drawer-checkout" disabled={sending || closed} onClick={checkout} style={{ width: "100%", marginTop: 12, minHeight: 58, background: closed ? "rgba(38,34,30,0.24)" : "#1A17E0", color: "#fff", border: "none", borderRadius: 4, fontWeight: 700, fontSize: 15, cursor: closed ? "not-allowed" : "pointer" }}>{sending ? "Mengunci…" : `Kunci Pesanan · ${count} porsi`}</button>
            <p style={{ fontSize: 11, color: "#9A9084", marginTop: 8, lineHeight: 1.5 }}>Bayar langsung ke rekening Rekan Masak lewat QR saat pesanan dikonfirmasi. Tanpa potongan komisi.</p>
          </div>
        )}
      </aside>
    </div>
  );
}
